import { createPortal } from 'react-dom';
import AgentPinWithdrawal from '../../auth/AgentPinWithdrawal';
import { agentPermitValid, agentTransferFingerprint } from '../../auth/agentSecurity';
import { useEffect, useRef, useState } from 'react';
import useAgentRecovery from './useAgentRecovery';
import banks from '../bankList';
import { createWithdrawalRequestId } from './walletWithdrawal';
import './VeritasonTransferModal.css';

// Use the existing bank codes, excluding card, insurance and other non-bank entries.
const bankCodes = new Set(['002', '003', '004', '007', '011', '012', '020', '023', '027', '031', '032', '034', '035', '037', '039', '045', '048', '050', '064', '071', '081', '088', '089', '090', '092']);
const transferBanks = banks.filter(bank => bankCodes.has(bank.code));
const money = value => Number(value ?? 0).toLocaleString('ko-KR');
const validMoney = value => (typeof value === 'number' || typeof value === 'string') && String(value).trim() !== '' && Number.isSafeInteger(Number(value)) && Number(value) >= 0;

function SummaryRow({ label, children }) {
    return <div className="row"><div className="row-label">{label}</div><div className="row-content">{children}</div></div>;
}

export default function VeritasonTransferModal({
    currentBalance, withdrawable, withdrawalFee, registeredAccount,
    transferError, isOutsideWithdrawWindow, withdrawWindow,
    onClose, onTransfer, onClearError, onLookup, pendingTransfer, onPendingChange,
    identity, restorePending = false, onQuery, onList, onRefresh,
    pinEnabled = false, onRegister,
}) {
    const recovery = useAgentRecovery({ enabled: restorePending, identity, api: process.env.REACT_APP_API, onQuery, onList, onRefresh });
    const [pinPermit,setPinPermit]=useState(null);
    const [,tick]=useState(0);
    useEffect(()=>{if(!pinEnabled)return;const timer=setInterval(()=>tick(n=>n+1),1000);return()=>clearInterval(timer);},[pinEnabled]);
    const [accountMode, setAccountMode] = useState('registered');
    const [bankCd, setBankCd] = useState('');
    const [account, setAccount] = useState('');
    const [input, setInput] = useState('');
    const [confirmation, setConfirmation] = useState(pendingTransfer ?? null);
    const [attempted, setAttempted] = useState(Boolean(pendingTransfer));
    const [status, setStatus] = useState(pendingTransfer ? '송금 결과를 확인해주세요.' : '');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const submitting = useRef(false);
    const dialogRef = useRef(null);
    const amount = Number(input.replace(/\D/g, '') || 0);
    const fundsReady = validMoney(withdrawable) && validMoney(withdrawalFee);
    const fee = Number(withdrawalFee ?? 0);
    const maxAmount = Math.max(0, Number(withdrawable ?? 0) - fee);
    const selectedBank = transferBanks.find(bank => bank.code === bankCd);
    const recipient = accountMode === 'registered' && registeredAccount ? registeredAccount : (
        selectedBank && /^\d{1,20}$/.test(account)
            ? { bankCd: selectedBank.code, bankName: selectedBank.name, account }
            : null
    );
    const recipientValid = Boolean(recipient?.bankCd && recipient?.bankName && recipient?.account);
    const amountValid = Number.isSafeInteger(amount) && amount > 0 && amount <= maxAmount;
    const blocked = recovery.blocked || !fundsReady || !recipientValid || !amountValid || isOutsideWithdrawWindow || isLoading;
    const outdated = confirmation && (!fundsReady || confirmation.fee !== fee || confirmation.amount > maxAmount);
    const confirming = Boolean(confirmation);
    const pinReady = !pinEnabled || (confirmation && agentPermitValid(pinPermit,confirmation));

    const close = () => { if (!submitting.current) onClose(); };
    const clearError = () => { setError(''); onClearError?.(); };

    useEffect(() => {
        dialogRef.current?.focus();
    }, [confirming, recovery.pending, recovery.blocked]);

    useEffect(() => {
        const onKey = event => {
            if (event.key === 'Escape' && !submitting.current) onClose();
            if (event.key === 'Tab' && dialogRef.current) {
                const controls = [...dialogRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled)')];
                const first = controls[0];
                const last = controls[controls.length - 1];
                if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
                    event.preventDefault(); last?.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault(); first?.focus();
                }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const lookup = async (target, transferAmount, transferFee) => {
        if (submitting.current) return;
        submitting.current = true;
        setIsLoading(true);
        clearError();
        setConfirmation(null);setPinPermit(null);
        try {
            const result = await onLookup(target);
            const data = result?.data;
            const expiresAt = Math.min(Date.parse(data?.expires_at), Date.now() + Number(data?.expires_in) * 1000);
            if (result?.code !== 1000 || !data?.bank_cd || !data?.bank_nm || !data?.accnt_no || !data?.accnt_nm || !data?.confirmation_token || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
                setError(result?.msg || '예금주 조회 결과를 확인하지 못했습니다. 다시 조회해주세요.');
                return;
            }
            setConfirmation({
                amount: transferAmount, fee: transferFee,
                recipient: { bankCd: data.bank_cd, bankName: data.bank_nm, account: data.accnt_no, accountName: data.accnt_nm },
                token: data.confirmation_token, expiresAt, requestId: createWithdrawalRequestId(),
            });
            setAttempted(false);
            setStatus('');
        } catch {
            setError('예금주 조회에 실패했습니다. 다시 조회해주세요.');
        } finally {
            submitting.current = false;
            setIsLoading(false);
        }
    };

    const review = () => {
        if (!blocked) lookup({ ...recipient }, amount, fee);
    };

    const transfer = async () => {
        if (!pinReady) return;
        if (!confirmation || submitting.current || (!attempted && (outdated || isOutsideWithdrawWindow))) return;
        if (!attempted && confirmation.expiresAt <= Date.now()) {
            await lookup(confirmation.recipient, confirmation.amount, fee);
            return; // A refreshed holder always requires a new explicit confirmation.
        }
        submitting.current = true;
        setIsLoading(true);
        clearError();
        if (restorePending) {
            try { await recovery.reserve(confirmation); }
            catch { setError('송금 요청키를 저장하지 못했거나 이전 송금이 있습니다. 결과를 확인해주세요.'); submitting.current=false;setIsLoading(false);return; }
        }
        setAttempted(true);
        onPendingChange?.(confirmation);
        try {
            const args=[confirmation.amount, confirmation.recipient, null, confirmation.requestId, confirmation.token];
            if(pinEnabled&&pinPermit?.required)args.push(pinPermit.authorization,pinPermit.csrfToken);
            const result = await onTransfer(...args);
            setPinPermit(null);
            if (restorePending) { recovery.settled(confirmation,result);onPendingChange?.(null);return; }
            if (result?.ok === true) {
                onPendingChange?.(null);
                onClose();
            } else if (result?.code === 4008) {
                setConfirmation(null);setPinPermit(null);
                setAttempted(false);
                onPendingChange?.(null);
                setStatus('');
                setError('계좌 확인이 만료되었거나 일치하지 않습니다. 예금주를 다시 조회해주세요.');
            } else if (result?.code === 1001 || result?.code === 1003) {
                setStatus(result.code === 1001 ? '송금 처리 중입니다.' : '송금 결과 확인 대기 중입니다.');
            } else {
                setError(result?.msg || '송금 처리 결과를 확인하지 못했습니다.');
                // A definitive validation rejection has not accepted the transfer.
                if (result?.code >= 4000 && result.code < 5000) {
                    setAttempted(false);
                    onPendingChange?.(null);
                }
            }
        } catch {
            if (restorePending) { recovery.settled(confirmation,null);onPendingChange?.(null); }
            setError('처리 결과를 확인하지 못했습니다. 송금 결과를 다시 확인해주세요.');
        } finally {
            submitting.current = false;
            setIsLoading(false);
        }
    };

    const back = () => {
        if (submitting.current || attempted) return;
        setConfirmation(null);setPinPermit(null);
        clearError();
    };

    if (restorePending && (recovery.pending || recovery.blocked)) return createPortal(<div className="modal-backdrop veritason-transfer-backdrop" onClick={close}><div className="modal-sheet veritason-transfer" ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="이전 송금 결과 확인" onClick={e=>e.stopPropagation()}>
        <div className="modal-head"><h2>{recovery.terminal ? '송금 결과' : '이전 송금 결과 확인'}</h2><button className="modal-close" disabled={recovery.busy} onClick={close} aria-label="닫기">✕</button></div>
        <div className="modal-body"><p role="status" className="veritason-transfer-intro">{recovery.message || '저장된 송금 요청을 확인하고 있습니다.'}</p>
        {recovery.pending && <><p style={{overflowWrap:'anywhere'}}>요청 번호: {recovery.pending.requestId}</p><p>이전 수취 계좌: {recovery.pending.bankName || '-'} {recovery.pending.accountLast4 ? `끝자리 ${recovery.pending.accountLast4}` : ''}</p><p>송금액: {validMoney(recovery.pending.amount) ? `${money(recovery.pending.amount)}원` : '-'}</p></>}
        {!recovery.terminal && <p className="veritason-transfer-intro">새로고침해도 송금은 취소되지 않습니다. 이전 요청의 결과가 확인될 때까지 새 송금을 진행하지 않습니다.</p>}</div>
        <div className="modal-foot"><button className="btn-transfer" disabled={recovery.busy} onClick={async()=>{if(recovery.terminal){setConfirmation(null);setAttempted(false);setStatus('');setInput('');onPendingChange?.(null);await recovery.restart();}else await recovery.query();}}>{recovery.busy?'확인 중…':recovery.terminal?'새 송금 준비':'송금 결과 조회'}</button></div>
    </div></div>, document.body);
    return createPortal(
        <div className="modal-backdrop veritason-transfer-backdrop" data-testid="veritason-transfer-backdrop" onClick={close}>
            <div className="modal-sheet veritason-transfer" role="dialog" aria-modal="true" aria-labelledby="veritason-transfer-title" aria-busy={isLoading} ref={dialogRef} tabIndex={-1} onClick={event => event.stopPropagation()}>
                <div className="modal-head">
                    {confirmation && <button className="veritason-transfer-back" onClick={back} disabled={isLoading || attempted} aria-label="송금 정보 수정">←</button>}
                    <h2 id="veritason-transfer-title">{confirmation ? '송금 정보 확인' : '이체'}</h2>
                    <button className="modal-close" onClick={close} disabled={isLoading} aria-label="닫기">✕</button>
                </div>
                <div className="modal-body">
                    {(error || transferError?.msg) && <div className="veritason-transfer-error" role="alert">{error || transferError.msg}</div>}
                    {status && <div className="veritason-transfer-intro" role="status">{status}</div>}
                    {isOutsideWithdrawWindow && <div className="veritason-transfer-error" role="alert">출금 가능 시간이 아닙니다.{withdrawWindow?.start && withdrawWindow?.end && ` ${withdrawWindow.start.slice(0, 5)}~${withdrawWindow.end.slice(0, 5)}에 출금 가능합니다.`}</div>}
                    {confirmation ? (
                        <>
                            <p className="veritason-transfer-intro">받는 계좌와 금액을 확인해주세요.</p>
                            <SummaryRow label="받는 은행"><strong>{confirmation.recipient.bankName}</strong></SummaryRow>
                            <SummaryRow label="계좌번호"><strong className="veritason-transfer-account-number">{confirmation.recipient.account}</strong></SummaryRow>
                            {confirmation.recipient.accountName && <SummaryRow label="예금주">{confirmation.recipient.accountName}</SummaryRow>}
                            <SummaryRow label="송금 금액"><strong>{money(confirmation.amount)}원</strong></SummaryRow>
                            <SummaryRow label="출금 수수료">{money(confirmation.fee)}원</SummaryRow>
                            <SummaryRow label="총 차감 금액"><strong>{money(confirmation.amount + confirmation.fee)}원</strong></SummaryRow>
                            {pinEnabled && !attempted && !outdated && <AgentPinWithdrawal key={agentTransferFingerprint(confirmation)} confirmation={confirmation} flow="BANK_ACCOUNT_V2" onReady={setPinPermit} onRegister={onRegister} />}
                            <p className="veritason-transfer-intro">{attempted ? '같은 송금 요청의 처리 결과를 확인합니다.' : '아래 버튼을 누르면 확인한 계좌로 송금됩니다.'}</p>
                            {outdated && !attempted && <div className="veritason-transfer-error" role="alert">잔액 또는 수수료가 변경되었습니다. 송금 정보를 다시 확인해주세요.</div>}
                        </>
                    ) : (
                        <>
                            <SummaryRow label="현재 잔고"><strong>{money(currentBalance)}원</strong></SummaryRow>
                            <SummaryRow label="출금가능 잔고"><strong>{fundsReady ? `${money(maxAmount)}원` : '-'}</strong></SummaryRow>
                            <SummaryRow label="출금 수수료">{validMoney(withdrawalFee) ? `${money(fee)}원` : '-'}</SummaryRow>
                            {!fundsReady && <p className="veritason-transfer-intro" role="status">잔액과 수수료 정보를 확인하고 있습니다.</p>}
                            <fieldset disabled={isLoading} style={{ border: 0, padding: 0, margin: 0 }}>
                            <div className="veritason-transfer-recipient">
                                <div className="veritason-transfer-label">받는 계좌</div>
                                {registeredAccount && <div className="veritason-transfer-tabs" role="group" aria-label="받는 계좌 선택">
                                    <button type="button" aria-pressed={accountMode === 'registered'} onClick={() => { setAccountMode('registered'); clearError(); }}>등록 계좌</button>
                                    <button type="button" aria-pressed={accountMode === 'direct'} onClick={() => { setAccountMode('direct'); clearError(); }}>직접 입력</button>
                                </div>}
                                {accountMode === 'registered' && registeredAccount ? (
                                    <div className="veritason-transfer-registered">{registeredAccount.bankName} {registeredAccount.account}{registeredAccount.accountName && <div>{registeredAccount.accountName}</div>}</div>
                                ) : (
                                    <>
                                        <div className="veritason-transfer-field">
                                            <label htmlFor="veritason-transfer-bank">은행</label>
                                            <select id="veritason-transfer-bank" value={bankCd} onChange={event => { setBankCd(event.target.value); clearError(); }}>
                                                <option value="">은행을 선택해주세요</option>
                                                {transferBanks.map(bank => <option key={bank.code} value={bank.code}>{bank.name}</option>)}
                                            </select>
                                        </div>
                                        <div className="veritason-transfer-field">
                                            <label htmlFor="veritason-transfer-account">계좌번호</label>
                                            <input id="veritason-transfer-account" type="text" inputMode="numeric" autoComplete="off" maxLength={20} placeholder="계좌번호 입력" value={account} onChange={event => { setAccount(event.target.value.replace(/[\s-]/g, '')); clearError(); }} aria-invalid={Boolean(account && !/^\d{1,20}$/.test(account))} />
                                        </div>
                                        {account && !/^\d{1,20}$/.test(account) && <div className="field-error" role="alert">계좌번호를 숫자로 입력해주세요.</div>}
                                    </>
                                )}
                            </div>
                            <SummaryRow label={<label htmlFor="veritason-transfer-amount">송금 금액</label>}>
                                <div className="amount-input-wrap">
                                    <input id="veritason-transfer-amount" className="amount-input" inputMode="numeric" autoComplete="off" placeholder="0" value={input} onChange={event => { const digits = event.target.value.replace(/\D/g, ''); setInput(digits ? money(digits) : ''); clearError(); }} />
                                    <span className="amount-suffix">원</span>
                                    <button className="btn-all" onClick={() => { setInput(money(maxAmount)); clearError(); }} disabled={!fundsReady}>전액</button>
                                </div>
                            </SummaryRow>
                            </fieldset>
                            <SummaryRow label="송금 후 출금가능 잔액"><strong>{fundsReady && Number.isSafeInteger(amount) ? `${money(Math.max(0, maxAmount - amount))}원` : '-'}</strong></SummaryRow>
                            {amount > maxAmount && <p className="field-error" role="alert">출금가능 잔고를 초과했습니다.</p>}
                        </>
                    )}
                </div>
                <div className="modal-foot">
                    <button className={`btn-transfer ${(confirmation ? (!attempted && (outdated || isOutsideWithdrawWindow || !pinReady)) || isLoading : blocked) ? 'btn-disabled' : ''}`} disabled={confirmation ? Boolean((!attempted && (outdated || isOutsideWithdrawWindow || !pinReady)) || isLoading) : blocked} onClick={confirmation ? transfer : review}>
                        {isLoading ? '처리 중...' : confirmation ? (attempted ? '송금 결과 확인' : '확인 후 송금') : '예금주 조회'}
                    </button>
                </div>
            </div>
        </div>, document.body
    );
}
